<script setup lang="ts">
import { onMounted, ref, computed } from 'vue';
import { useTicketsComposable } from '@/components/TicketsAccountingPeriod/useTicketsComposable';
import type { AccountingTicketTemplateType } from '@/generated/graphql';

const emit = defineEmits(['loaded', 'select']);

const { getAccountingTicketTemplates } = useTicketsComposable();

const loading = ref(true);
const error = ref<string | null>(null);
const templates = ref<AccountingTicketTemplateType[]>([]);
const query = ref('');
const expanded = ref(false);

const filteredTemplates = computed(() => {
  const q = query.value.trim().toLowerCase();
  if (!q) return templates.value;
  return templates.value.filter(t =>
    t.name?.toLowerCase().includes(q) ||
    t.subject?.toLowerCase().includes(q) ||
    t.message?.toLowerCase().includes(q),
  );
});

const loadTemplates = async (): Promise<AccountingTicketTemplateType[]> => {
  try {
    const tpl = await getAccountingTicketTemplates();
    return (tpl || []) as AccountingTicketTemplateType[];
  } catch (err) {
    console.error('Failed to load accounting ticket templates', err);
    throw err;
  }
};

const handleSelect = (tpl: AccountingTicketTemplateType) => {
  emit('select', tpl);
  expanded.value = false;
};

onMounted(async () => {
  loading.value = true;
  error.value = null;
  try {
    const tpl = await loadTemplates();
    templates.value = tpl;
    emit('loaded', tpl);
  } catch (err) {
    console.error('Failed to load templates', err);
  } finally {
    loading.value = false;
  }
});
</script>

<template>
  <div class="q-ma-md bg-grey-1 q-pa-md rounded-borders bordered">
    <q-expansion-item
      v-model="expanded"
      icon="article"
      label="Use a template"
      header-class="rounded-borders"
      expand-separator
      :duration="350"
    >
      <div class="q-pa-sm">
        <div class="row items-center q-gutter-sm q-mb-sm">
          <q-input
            v-model="query"
            dense
            outlined
            placeholder="Search templates..."
            class="col"
          />
        </div>

        <div
          v-if="loading"
          class="q-gutter-y-sm"
        >
          <q-skeleton
            type="rect"
            height="28px"
          />
          <q-skeleton
            type="rect"
            height="28px"
          />
          <q-skeleton
            type="rect"
            height="28px"
          />
        </div>

        <q-banner
          v-else-if="error"
          class="bg-negative text-white q-pa-sm rounded-borders"
        >
          {{ error }}
        </q-banner>

        <div v-else>
          <q-banner
            v-if="filteredTemplates.length === 0"
            class="bg-grey-3 text-grey-9 q-pa-sm rounded-borders"
          >
            No templates found
          </q-banner>
          <q-list
            v-else
            bordered
            separator
            class="rounded-borders overflow-hidden"
          >
            <q-item
              v-for="tpl in filteredTemplates"
              :key="tpl.id"
              clickable
              @click="handleSelect(tpl)"
            >
              <q-item-section>
                <q-item-label class="text-weight-medium">
                  {{ tpl.subject }}
                </q-item-label>
                <q-item-label
                  caption
                  lines="2"
                  class="text-grey-7"
                >
                  {{ tpl.message }}
                  <q-tooltip
                    anchor="top middle"
                    self="bottom middle"
                    :offset="[0, 8]"
                    max-width="480px"
                    class="bg-grey-10 text-white text-body2"
                  >
                    <div class="tooltip-pre">
                      {{ tpl.message }}
                    </div>
                  </q-tooltip>
                </q-item-label>
              </q-item-section>
              <q-item-section side>
                <q-btn
                  flat
                  dense
                  no-caps
                  color="primary"
                  label="Use"
                  @click.stop="handleSelect(tpl)"
                />
              </q-item-section>
            </q-item>
          </q-list>
        </div>
      </div>
    </q-expansion-item>
  </div>
</template>

<style scoped>
:deep(.q-expansion-item__content) {
  will-change: height;
}

.tooltip-pre {
  white-space: pre-wrap;
  word-break: break-word;
}
</style>
